import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useProject } from "@/api/hooks";
import { useStatuses } from "@/api/work";
import { ConfirmDialog } from "@/components/Dialog";
import { unverifiedIfDone, type GuardTask } from "@/lib/testing";

type Ask = (keys: string[], total: number) => Promise<boolean>;

const AskContext = createContext<Ask>(async () => true);

/** Hosts the one "move untested work to Done?" dialog the status controls share. */
export function DoneGuardProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<{ keys: string[]; total: number } | null>(null);
  const resolve = useRef<(ok: boolean) => void>(() => {});
  const ask = useCallback<Ask>(
    (keys, total) =>
      new Promise((done) => {
        resolve.current = done;
        setPending({ keys, total });
      }),
    [],
  );
  const finish = (ok: boolean) => {
    setPending(null);
    resolve.current(ok);
  };
  const keys = pending?.keys ?? [];
  return (
    <AskContext.Provider value={ask}>
      {children}
      <ConfirmDialog
        open={!!pending}
        onClose={() => finish(false)}
        onConfirm={() => finish(true)}
        title={t("doneGuard.title")}
        body={
          keys.length === 1 && pending?.total === 1
            ? t("doneGuard.one", { key: keys[0] })
            : t("doneGuard.many", { count: keys.length, total: pending?.total ?? 0, keys: keys.slice(0, 8).join(", ") + (keys.length > 8 ? "…" : "") })
        }
        confirmLabel={t("doneGuard.confirm")}
      />
    </AskContext.Provider>
  );
}

/**
 * Wraps a status change: moving tasks with unpassed testing to a done status asks first, in projects that use the
 * testing flow. The server accepts the move either way; the task then shows "not verified".
 */
export function useDoneGuard(projectKey: string | null | undefined) {
  const ask = useContext(AskContext);
  const project = useProject(projectKey ?? undefined);
  const statuses = useStatuses(projectKey ?? "");
  return useCallback(
    (tasks: GuardTask[], statusId: string, run: () => void) => {
      const target = statuses.data?.find((s) => s.id === statusId);
      const flowOn = !!project.data?.testing_status_id;
      const unverified = target?.category === "done" && flowOn ? tasks.filter(unverifiedIfDone) : [];
      if (!unverified.length) return run();
      void ask(
        unverified.map((x) => x.key),
        tasks.length,
      ).then((ok) => ok && run());
    },
    [ask, project.data, statuses.data],
  );
}
